/**
 * OperationalLineageGraphCanvas
 *
 * SVG canvas with deterministic layout, zoom/pan via viewBox,
 * node selection, hover metadata capsules, and edge rendering.
 * Same graph hash → same layout. No random placement.
 */

import React, { useState, useMemo, useCallback, useRef } from 'react';
import type { OperationalLineageGraph, LineageNode, LineageEdge } from '../operationalLineageTypes.js';
import { OperationalLineageNodeRenderer, computeDeterministicLayout, type LayoutNode } from './OperationalLineageNodeRenderer.js';
import { OperationalLineageEdgeRenderer, layoutEdges, type LayoutEdge } from './OperationalLineageEdgeRenderer.js';
import { OperationalLineageGraphLegend } from './OperationalLineageGraphLegend.js';
import { OperationalLineageGraphControls } from './OperationalLineageGraphControls.js';
import { OperationalLineageMiniMap } from './OperationalLineageMiniMap.js';
import { NEUTRAL, TYPOGRAPHY, SPACING } from '../../../modules/governanceEventStream/operationalVisualLanguage/index.js';
import { TRANSITION_GRAPH_NODE, TRANSITION_GRAPH_EDGE, TRANSITION_HOVER_CAPSULE } from '../../motion/motionTokens.js';

interface Props {
  graph: OperationalLineageGraph;
  selectedNodeId?: string | null;
  onSelectNode?: (node: LineageNode) => void;
}

const DEFAULT_ZOOM = 1;
const MIN_ZOOM = 0.4;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.2;

export const OperationalLineageGraphCanvas: React.FC<Props> = ({ graph, selectedNodeId, onSelectNode }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [hoveredNode, setHoveredNode] = useState<LayoutNode | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  const layoutNodes = useMemo(() => computeDeterministicLayout(graph.nodes), [graph.nodes]);
  const layoutEdgesList = useMemo(() => layoutEdges(graph.edges, layoutNodes), [graph.edges, layoutNodes]);

  const selectedNode = useMemo(
    () => layoutNodes.find((n) => n.id === selectedNodeId) || null,
    [layoutNodes, selectedNodeId]
  );

  const boundingBox = useMemo(() => {
    const xs = layoutNodes.map((n) => n.x);
    const ys = layoutNodes.map((n) => n.y);
    const minX = Math.min(...xs) - 80;
    const maxX = Math.max(...xs) + 80;
    const minY = Math.min(...ys) - 40;
    const maxY = Math.max(...ys) + 60;
    return { minX, maxX, minY, maxY, width: maxX - minX, height: maxY - minY };
  }, [layoutNodes]);

  const handleZoomIn = useCallback(() => setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM)), []);
  const handleZoomOut = useCallback(() => setZoom((z) => Math.max(z - ZOOM_STEP, MIN_ZOOM)), []);
  const handleReset = useCallback(() => {
    setZoom(DEFAULT_ZOOM);
    setPan({ x: 0, y: 0 });
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    dragStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  }, [pan]);

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!isDragging) return;
    setPan({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y });
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent<SVGSVGElement>) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setZoom((z) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z + delta)));
  }, []);

  const viewBox = useMemo(() => {
    const w = boundingBox.width / zoom;
    const h = boundingBox.height / zoom;
    const cx = (boundingBox.minX + boundingBox.maxX) / 2 - pan.x / zoom;
    const cy = (boundingBox.minY + boundingBox.maxY) / 2 - pan.y / zoom;
    return `${cx - w / 2} ${cy - h / 2} ${w} ${h}`;
  }, [boundingBox, zoom, pan]);

  const handleNodeClick = useCallback(
    (node: LayoutNode) => {
      onSelectNode?.(node);
    },
    [onSelectNode]
  );

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundColor: NEUTRAL.bg100,
        overflow: 'hidden',
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
      }}
      data-testid="lineage-graph-canvas"
    >
      {/* SVG graph */}
      <svg
        ref={svgRef}
        style={{
          width: '100%',
          height: '100%',
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
        viewBox={viewBox}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        {/* CSS for continuity transition animation */}
        <defs>
          <style>{`
            @keyframes lineageContinuityFlow {
              from { stroke-dashoffset: 18; }
              to { stroke-dashoffset: 0; }
            }
          `}</style>
        </defs>

        {/* Edges first (behind nodes) */}
        {layoutEdgesList.map((edge) => (
          <OperationalLineageEdgeRenderer
            key={edge.id}
            edge={edge}
            isSelected={selectedNodeId ? edge.source === selectedNodeId || edge.target === selectedNodeId : false}
          />
        ))}

        {/* Nodes */}
        {layoutNodes.map((node) => (
          <OperationalLineageNodeRenderer
            key={node.id}
            node={node}
            isSelected={node.id === selectedNodeId}
            isHovered={node.id === hoveredNode?.id}
            onClick={handleNodeClick}
            onMouseEnter={setHoveredNode}
            onMouseLeave={() => setHoveredNode(null)}
          />
        ))}
      </svg>

      {/* Hover metadata capsule */}
      {hoveredNode && (
        <div
          style={{
            position: 'absolute',
            top: SPACING.sp3,
            left: SPACING.sp3,
            padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
            backgroundColor: NEUTRAL.bg300,
            border: `1px solid ${NEUTRAL.border}`,
            borderRadius: 3,
            fontSize: TYPOGRAPHY.micro.size,
            color: NEUTRAL.text500,
            fontFamily: 'system-ui, sans-serif',
            pointerEvents: 'none',
            zIndex: 10,
            maxWidth: 280,
            transition: TRANSITION_HOVER_CAPSULE,
          }}
          data-testid="lineage-hover-capsule"
        >
          <div style={{ fontWeight: 600, color: NEUTRAL.text600, marginBottom: SPACING.sp1 }}>
            {hoveredNode.type}
          </div>
          <div style={{ color: NEUTRAL.text400, fontFamily: TYPOGRAPHY.mono.family }}>
            {hoveredNode.label.length > 40 ? hoveredNode.label.slice(0, 40) + '…' : hoveredNode.label}
          </div>
          {hoveredNode.hash && (
            <div style={{ color: NEUTRAL.text300, marginTop: SPACING.sp1, fontFamily: TYPOGRAPHY.mono.family }}>
              {hoveredNode.hash.slice(0, 24)}…
            </div>
          )}
        </div>
      )}

      {/* Controls */}
      <div style={{ position: 'absolute', bottom: SPACING.sp3, right: SPACING.sp3, zIndex: 10 }}>
        <OperationalLineageGraphControls
          zoom={zoom}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onReset={handleReset}
        />
      </div>

      {/* Legend */}
      <div style={{ position: 'absolute', bottom: SPACING.sp3, left: SPACING.sp3, zIndex: 10 }}>
        <OperationalLineageGraphLegend />
      </div>

      {/* Minimap */}
      <div style={{ position: 'absolute', top: SPACING.sp3, right: SPACING.sp3, zIndex: 10 }}>
        <OperationalLineageMiniMap
          layoutNodes={layoutNodes}
          layoutEdges={layoutEdgesList}
          selectedNodeId={selectedNodeId}
          viewBox={boundingBox}
        />
      </div>
    </div>
  );
};
